import { WalletAddress } from '../Ether';
import { FollowRelationship } from './Follows';
import { GemzUser } from './User';

export interface GameCommentSchema {
  id: number;
  created_at?: string;
  updated_at?: string;
  user_id: number;
  offchain_game_id: string;
  reply_to_id?: number | null;
  top_id?: number | null;
  message: string;
  user_follows_related?: boolean;
  related_follows_user?: boolean;
  user_relationship?: FollowRelationship;
  user_relation_to?: number;
  reply_count?: number | null;
}

export type InsertGameCommentSchema = Omit<GameCommentSchema, 'id' | 'user_relationship' | 'user_relation_to' | 'reply_count'>;
export type UpdateGameCommentSchema = Omit<GameCommentSchema, 'user_relationship' | 'user_relation_to' | 'reply_count'>;

export interface GameCommentUserSchema {
  user_id: number;
  user_username: string;
  user_image: string;
  user_wallet_address: WalletAddress;
  user_displayname: string;
}

export interface GameCommentData {
  id: number;
  createdAt?: string;
  updatedAt?: string;
  userId: number;
  offChainGameId: string;
  replyToId?: number | null;
  topId?: number | null;
  message: string;
  user?: Partial<GemzUser>;
  userRelationship?: FollowRelationship;
  userRelationTo?: number;
  replies?: GameCommentData[];
  replyCount?: number | null;
}

export type InsertGameCommentData = Omit<GameCommentData, 'id' | 'user' | 'userRelationship' | 'userRelationTo' | 'replies' | 'replyCount' | 'topId'>;
export type UpdateGameCommentData = Omit<GameCommentData, 'user' | 'userRelationship' | 'userRelationTo' | 'replies' | 'replyCount' | 'topId'>;

function passThroughIfNotNumber(value: unknown): number | undefined | null {
  if (typeof value === 'undefined') {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  return Number(value);
}

export const getUserFromCommentUserSchema = (schema: GameCommentUserSchema): Partial<GemzUser> => ({
  id: Number(schema.user_id),
  username: schema.user_username,
  twitter: {
    handle: schema.user_username,
    displayName: schema.user_displayname,
    picture: schema.user_image,
  },
  walletAddress: schema.user_wallet_address,
});

export const getGameCommentDataFromSchema = (schema: GameCommentSchema, extra?: {
  userSchema?: GameCommentUserSchema,
  userData?: Partial<GemzUser>,
}): GameCommentData => ({
  id: Number(schema.id),
  createdAt: schema.created_at,
  updatedAt: schema.updated_at,
  userId: Number(schema.user_id),
  offChainGameId: schema.offchain_game_id,
  replyToId: passThroughIfNotNumber(schema.reply_to_id),
  replyCount: passThroughIfNotNumber(schema.reply_count),
  topId: passThroughIfNotNumber(schema.top_id),
  message: schema.message,
  userRelationship: schema.user_relationship,
  userRelationTo: Number(schema.user_relation_to),
  user: extra?.userData ?? (extra?.userSchema ? getUserFromCommentUserSchema(extra.userSchema) : undefined),
});

export const getGameCommentSchemaFromData = (data: GameCommentData): GameCommentSchema => ({
  id: data.id,
  created_at: data.createdAt,
  updated_at: data.updatedAt,
  user_id: data.userId,
  offchain_game_id: data.offChainGameId,
  reply_to_id: data.replyToId,
  top_id: data.topId,
  message: data.message,
});
